/**
 * Error Handler Utility
 * Maps API errors to user-friendly messages
 */

// Common error message mappings
const ERROR_MESSAGES = {
  // Authentication errors
  'Invalid credentials': 'Incorrect PIN. Please try again.',
  'Invalid PIN': 'Incorrect PIN. Please try again.',
  'User not found': 'No account found with this email or phone number.',
  'Account not found': 'No account found with this email or phone number.',
  'Email already exists': 'An account with this email already exists. Try logging in instead.',
  'Phone already exists': 'An account with this phone number already exists. Try logging in instead.',
  'Invalid email': 'Please enter a valid email address.',
  'Invalid phone': 'Please enter a valid phone number.',

  // OTP errors
  'Invalid OTP': 'The verification code is incorrect. Please try again.',
  'OTP expired': 'The verification code has expired. Please request a new one.',
  'Invalid verification code': 'The verification code is incorrect. Please try again.',

  // Rate limiting
  'Rate limited': 'Too many attempts. Please wait a moment and try again.',
  'Too many requests': 'Too many attempts. Please wait a moment and try again.',

  // Network errors
  'Network error': 'Unable to connect. Please check your internet connection.',
  'Failed to fetch': 'Unable to connect. Please check your internet connection.',
  'NetworkError': 'Unable to connect. Please check your internet connection.',

  // Session errors
  'Session expired': 'Your session has expired. Please log in again.',
  'Token expired': 'Your session has expired. Please log in again.',
  'Unauthorized': 'Please log in to continue.',

  // PIN errors
  'PIN already set': 'A PIN has already been created for this account.',
  'Invalid current PIN': 'The current PIN you entered is incorrect.',
  'PIN too simple': 'Please choose a more secure PIN (avoid sequences like 1234).',

  // BoldTag errors
  'BoldTag taken': 'This BoldTag is already taken. Please choose another.',
  'Invalid BoldTag': 'Please enter a valid BoldTag (letters, numbers, and underscores only).',

  // Referral errors
  'Invalid referral code': 'The referral code you entered is invalid.',
  'Referral code expired': 'This referral code has expired.',

  // Server errors
  'Internal server error': 'Something went wrong. Please try again later.',
  'Service unavailable': 'Service is temporarily unavailable. Please try again later.',
};

/**
 * Get user-friendly error message
 * @param {Error|string} error - Error object or message
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
  const message = typeof error === 'string' ? error : error?.message || 'An unexpected error occurred';

  // Check for exact match
  if (ERROR_MESSAGES[message]) {
    return ERROR_MESSAGES[message];
  }

  // Check for partial match (case-insensitive)
  const lowerMessage = message.toLowerCase();
  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }

  // Check for HTTP status codes in message
  if (message.includes('401') || message.includes('Unauthorized')) {
    return ERROR_MESSAGES['Unauthorized'];
  }
  if (message.includes('429')) {
    return ERROR_MESSAGES['Rate limited'];
  }
  if (message.includes('500') || message.includes('Internal')) {
    return ERROR_MESSAGES['Internal server error'];
  }
  if (message.includes('503') || message.includes('unavailable')) {
    return ERROR_MESSAGES['Service unavailable'];
  }

  // Return original message if no mapping found
  // But clean up technical details
  if (message.startsWith('Request failed:')) {
    return 'Something went wrong. Please try again.';
  }

  return message;
}

/**
 * Check if error is a network error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export function isNetworkError(error) {
  const message = error?.message?.toLowerCase() || '';
  return (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('offline') ||
    !navigator.onLine
  );
}

/**
 * Check if error is an authentication error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export function isAuthError(error) {
  const message = error?.message?.toLowerCase() || '';
  return (
    message.includes('401') ||
    message.includes('unauthorized') ||
    message.includes('session expired') ||
    message.includes('token expired')
  );
}

/**
 * Check if error is a validation error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export function isValidationError(error) {
  const message = error?.message?.toLowerCase() || '';
  return (
    message.includes('invalid') ||
    message.includes('required') ||
    message.includes('already exists')
  );
}

/**
 * Check if error is rate limiting
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export function isRateLimitError(error) {
  const message = error?.message?.toLowerCase() || '';
  return message.includes('429') || message.includes('rate') || message.includes('too many');
}

/**
 * Format field-level validation errors
 * @param {Object} errors - Error object with field names as keys
 * @returns {Object} Formatted errors
 */
export function formatFieldErrors(errors) {
  if (!errors || typeof errors !== 'object') {
    return {};
  }

  const formatted = {};
  for (const [field, messages] of Object.entries(errors)) {
    // Handle array of messages
    if (Array.isArray(messages)) {
      formatted[field] = messages[0]; // Take first message
    } else if (typeof messages === 'string') {
      formatted[field] = messages;
    } else if (messages?.message) {
      formatted[field] = messages.message;
    }
  }

  return formatted;
}

export default {
  getErrorMessage,
  isNetworkError,
  isAuthError,
  isValidationError,
  isRateLimitError,
  formatFieldErrors,
};
